import { useQueryClient } from "@tanstack/react-query";
import { DevSignIn } from "@/features/authentication/dev-sign-in";
import { LoginView } from "@/features/authentication/login-view";
import { returnPath } from "@/lib/navigation";

/**
 * Where hosted sign-in begins, carrying where to return to afterwards — which
 * is how somebody who arrived on an invitation lands back on it.
 */
export function startHref(returnTo: string, loginHint?: string): string {
  const hint = loginHint ? `&loginHint=${encodeURIComponent(loginHint)}` : "";
  return `/api/auth/oidc/start?returnTo=${encodeURIComponent(returnPath(returnTo))}${hint}`;
}

/**
 * The sign-in surface.
 *
 * With an identity provider configured this is the application's own login
 * screen handing off to hosted OIDC, exactly as before. Locally, where no
 * provider exists, it offers the development identity provider instead — which
 * the API refuses to enable in production.
 */
export function SignIn({ authMode }: { authMode: "dev" | "oidc" }) {
  const queryClient = useQueryClient();
  // The provider reports a refusal by sending the browser back with a reason.
  const error = new URLSearchParams(window.location.search).get("error") ?? undefined;

  const configured = authMode !== "dev";

  if (!configured) {
    return <DevSignIn onSignedIn={() => void queryClient.invalidateQueries()} />;
  }

  return (
    <LoginView
      configured
      error={error}
      // A navigation, not a fetch: the endpoint answers with a redirect to the
      // provider and sets the cookie carrying this login's state, nonce and
      // PKCE verifier. Fetching it would follow the redirect in the background
      // and leave the person sitting on the page they started from.
      signIn={async () =>
        window.location.assign(
          startHref(window.location.pathname + window.location.search + window.location.hash),
        )
      }
    />
  );
}
